import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Star, StarOff, Folder } from 'lucide-react';
import 'react-quill/dist/quill.snow.css';
import Cookies from "js-cookie";
import axios from "axios";
import { useNavigate, useParams } from 'react-router-dom';
import ItemDetailsModal from "./ItemDetailsModal";
import { configureKeyboardNavigation } from "../redux/actions/global.action";
import { BACKEND_API_URL } from "../utils/api";
import { stringToColor, getRelativeTime, formatDateTime, isAndroidDevice } from "../utils/utils";
import { getPrimarySortDateField } from "../constants/canvasSort";
import { cn } from "@/lib/utils";
import { useTouchGestures } from "../hooks/useTouchGestures";

// Zustand Store
import useCanvasStore from "../stores/canvasStore";

// Android browser: long tap toggles the folder in focus mode, double tap opens its details popup
const IS_ANDROID = isAndroidDevice();
// Block the long-press callout (search / copy menu) on the folder row
const ANDROID_ROW_STYLE = { WebkitTouchCallout: 'none', WebkitUserSelect: 'none' };

/**
 * FolderItem - Displays a folder in the canvas
 *
 * Props (minimal - reads most data from store):
 * - id: folder ID
 * - depth: depth level in the canvas
 * - itemIndex: index in the items array for keyboard navigation
 * - path_string: optional path string for search results
 * - parents: optional parent breadcrumbs for search results
 */
const FolderItem = ({
    id,
    depth,
    itemIndex,
    path_string,
    parents
}) => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const folderRef = useRef(null);
    const { folderId } = useParams();

    // User settings from Redux (auth)
    const user_settings = useSelector((state) => state.auth.user_settings);
    const isMinimalView = user_settings?.minimal_mode;

    // Read item data and state from Zustand store
    const item = useCanvasStore(s => s.items[depth]?.find(i => i.id === id));
    const selectedPath = useCanvasStore(s => s.selectedPath);
    const selectedItems = useCanvasStore(s => s.selectedItems);
    const focusedIndex = useCanvasStore(s => s.focusedIndex);
    const openDetailPaneId = useCanvasStore(s => s.openDetailPaneId);
    const toggleItemSelection = useCanvasStore(s => s.toggleItemSelection);
    const selectItemRange = useCanvasStore(s => s.selectItemRange);
    const selectSingleItem = useCanvasStore(s => s.selectSingleItem);
    const setFocusedIndex = useCanvasStore(s => s.setFocusedIndex);
    const setCurrentDepth = useCanvasStore(s => s.setCurrentDepth);
    const setOpenDetailPaneId = useCanvasStore(s => s.setOpenDetailPaneId);
    const sortFields = useCanvasStore(s => s.sortFields);
    const focusMode = useCanvasStore(s => s.focusMode);
    const setFocusMode = useCanvasStore(s => s.setFocusMode);

    // Unread count from store
    const unreadCountData = useCanvasStore(s => s.unreadCounts[id]);
    const unreadCount = unreadCountData?.unread_count || 0;

    // Local state
    const [folderData, setFolderData] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const nameRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    // Derived values from store
    const data = item || {};
    const name = data.name || '';
    const is_archived = data.is_archived || false;
    const agg_data = data.aggregator_data;

    // Show date only when created_at or updated_at is the primary (first) sort field
    const primarySortDateField = useMemo(
        () => getPrimarySortDateField(sortFields),
        [sortFields]
    );
    const primarySortDateValue = primarySortDateField ? data[primarySortDateField] : null;
    const sortDateLabel = primarySortDateField === 'updated_at' ? 'Updated' : 'Created';

    // Check if this item is focused via keyboard navigation
    const isFocused = focusedIndex === `${depth}_${itemIndex}`;

    const isSelected = selectedItems.length > 0 &&
        selectedItems[0].depth === depth &&
        selectedItems.some(item => item.id === id);

    // Check if this folder is the current folder (last in selectedPath)
    const isCurrentFolder = useMemo(() => {
        if (!selectedPath || selectedPath.length === 0) return false;
        return selectedPath[selectedPath.length - 1] === id;
    }, [selectedPath, id]);

    // Check if this folder is in the current path
    const isInPath = selectedPath && selectedPath.includes(id);

    const handleOpen = useCallback(() => {
        setIsOpen(true);
        dispatch(configureKeyboardNavigation(false))
    }, [dispatch]);

    const focusThisItem = () => {
        if (itemIndex !== undefined && depth !== undefined) {
            setFocusedIndex(`${depth}_${itemIndex}`);
            setCurrentDepth(depth);
        }
    };

    // Select this folder and open it on the canvas (search results open in a new tab)
    const openFolder = () => {
        if (depth !== -1) {
            // Select this folder (same options as Ctrl+Click), replacing any existing selection
            selectSingleItem({
                id: id,
                parent_id: data.parent_id,
                item_type: data.item_type,
                name: name,
                is_archived: is_archived,
                employees: data.employees || []
            }, depth, itemIndex);
            // Navigate to folder - fetchCanvas will be triggered by Home component
            history(`/folder/${id}`);
        } else {
            const folderUrl = `/folder/${id}`;
            window.open(folderUrl, '_blank');
        }
    };

    // Open this folder's channel in focus mode
    const openFolderInFocusMode = () => {
        setFocusMode(true);
        if (depth === -1) {
            // Search result: a long tap can't open a new tab (popup blocker), open it here
            history(`/folder/${id}`);
            return;
        }
        openFolder();
    };

    // Long tap: open the folder in focus mode, or leave focus mode if it is already open there
    const handleFolderLongTap = () => {
        focusThisItem();
        const isOpenInFocusMode = focusMode && folderId === String(id);
        if (isOpenInFocusMode) {
            setFocusMode(false);
        } else {
            openFolderInFocusMode();
        }
    };

    const handleFolderDoubleTap = () => {
        focusThisItem();
        handleOpen();
    };

    const { touchHandlers, consumeTouchContextMenu } = useTouchGestures({
        enabled: IS_ANDROID,
        gestureKey: id,
        onLongTap: handleFolderLongTap,
        onDoubleTap: handleFolderDoubleTap,
    });

    const handleFolderClick = (e) => {
        e.preventDefault();

        // Set focus to this item on any click
        focusThisItem();

        // Multi-select mode: Ctrl+Click (or Cmd+Click on Mac) - toggle single item
        if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
            e.stopPropagation();
            toggleItemSelection({
                id: id,
                parent_id: data.parent_id,
                depth: depth,
                item_type: data.item_type,
                name: name,
                is_archived: is_archived,
                employees: data.employees || []
            }, depth, itemIndex);
            return;
        }

        // Range select: Shift+Click (with or without Ctrl) - select all items from anchor to clicked
        if (e.shiftKey) {
            e.stopPropagation();
            selectItemRange({
                id: id,
                parent_id: data.parent_id,
                item_type: data.item_type,
                name: name,
                is_archived: is_archived,
                employees: data.employees || []
            }, depth, itemIndex);
            return;
        }

        // Normal click behavior
        if (e.type === 'click' && e.detail === 2) { // Double-click
            handleOpen();
        } else if (e.type === 'contextmenu') { // Right-click
            e.preventDefault();
            handleOpen();
        } else {
            openFolder();
        }
    };

    const handleFolderContextMenu = (e) => {
        // Touch long-press is handled by the touch gestures - don't open the popup for it
        if (consumeTouchContextMenu(e)) return;
        handleFolderClick(e);
    };

    const fetchFolderDetails = useCallback(async () => {
        try {
            const access_token = Cookies.get("access_token")
            const response = await axios.get(`${BACKEND_API_URL}/api/folder/${id}`, {
                headers: {
                    'Authorization': `Token ${access_token}`,
                    'Content-Type': 'application/json',
                },
            });
            setFolderData(response.data);
        } catch (error) {
            console.error('Error fetching folder details:', error);
        }
    }, [id]);

    useEffect(() => {
        if (isOpen) {
            fetchFolderDetails();
        }
    }, [isOpen, fetchFolderDetails]);

    useEffect(() => {
        const checkTruncation = () => {
            if (nameRef.current) {
                setIsTruncated(nameRef.current.scrollWidth > nameRef.current.clientWidth);
            }
        };
        checkTruncation();
        window.addEventListener('resize', checkTruncation);
        return () => window.removeEventListener('resize', checkTruncation);
    }, [name]);

    // Watch openDetailPaneId from Zustand and open modal when this item's ID matches
    useEffect(() => {
        if (openDetailPaneId === id && !isOpen) {
            handleOpen();
        }
    }, [openDetailPaneId, id, isOpen, handleOpen]);

    const handleClose = () => {
        setIsOpen(false);
        setOpenDetailPaneId(null); // Clear the detail pane ID when closing
        dispatch(configureKeyboardNavigation(true));
    };

    const get_status = () => {
        let status = data.status;
        if (!status) return '';
        const custom_status_order = ["Taking Longer", "Doing", "Doing-in-bg", "Paused", "TODO", "Block-on-asm",
            "Blocked-on-purchase", "Blocked-on-discussion", "Blocked", "Done"]
        if (status.length <= 2) {
            status = custom_status_order[parseInt(status, 10) - 1];
        }
        return status
    }

    const getStatusColor = () => {
        const status = get_status();
        const statusColors = {
            "Taking Longer": "#FF6B6B",
            "Doing": "#4CAF50",
            "Doing-in-bg": "#8BC34A",
            "Paused": "#FFA726",
            "TODO": "#64B5F6",
            "Block-on-asm": "#EF5350",
            "Blocked-on-purchase": "#EC407A",
            "Blocked-on-discussion": "#AB47BC",
            "Blocked": "#F44336",
            "Done": "#66BB6A"
        };
        return statusColors[status] || '#9E9E9E';
    }


    const handleUpdateFolderData = (updatedData) => {
        setFolderData(updatedData);
    };

    const renderAggregatorChips = () => {
        if (!agg_data) {
            return null;
        }

        return Object.keys(agg_data).map((key, index) => (
            <span
                key={index}
                className="inline-flex items-center px-1.5 py-0 text-[9px] rounded border border-primary/30 bg-primary/5 text-primary"
            >
                {key}: {agg_data[key]}
            </span>
        ));
    }

    // Don't render if item doesn't exist in store
    if (!item) return null;

    return (
        <>
            <div data-name="folder-item-container" className="flex w-full min-w-0">
                <div
                    data-name="folder-item-main"
                    data-item-id={id}
                    ref={folderRef}
                    onClick={handleFolderClick}
                    onContextMenu={handleFolderContextMenu}
                    {...touchHandlers}
                    style={IS_ANDROID ? ANDROID_ROW_STYLE : undefined}
                    className={cn(
                        "folder-container w-full flex",
                        // No double-tap zoom or text selection on the folder,
                        // so long tap and double tap reach the gestures
                        IS_ANDROID && "touch-manipulation select-none",
                        parents && "w-[40%]",
                        // Highlight folders in the current path
                        isInPath && "in-path",
                        // Selection and focus states
                        isSelected && isCurrentFolder && depth !== -1 && "selected focused",
                        isSelected && !isCurrentFolder && "selected",
                        isCurrentFolder && depth !== -1 && !isSelected && "focused",
                        // Keyboard navigation focus
                        isFocused && "ring-2 ring-primary ring-offset-1"
                    )}
                >
                    {/* Left content */}
                    <div className="flex-1 min-w-0 flex flex-col items-start">
                        {/* Header row */}
                        <div className="flex items-center gap-1 w-full overflow-hidden">
                            <span className="flex-shrink-0">
                                {data.star_level > 0 ?
                                    <Star className={cn(
                                        "w-4 h-4 fill-current",
                                        is_archived ? "text-muted-foreground" :
                                            data.star_level === 3 ? "text-destructive" :
                                                data.star_level === 2 ? "text-warning" : "text-success"
                                    )} /> :
                                    <StarOff className={cn(
                                        "w-4 h-4",
                                        is_archived ? "text-muted-foreground" : "text-muted-foreground"
                                    )} />
                                }
                            </span>
                            <Folder
                                className="w-4 h-4 flex-shrink-0"
                                style={{ color: getStatusColor() }}
                            />
                            <div className="min-w-0 flex-1">
                                {isTruncated ? (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span ref={nameRef} className={cn(
                                                "text-sm truncate block select-none cursor-pointer",
                                                is_archived ? "text-muted-foreground" : "text-foreground"
                                            )}>
                                                {name}
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent
                                            side="top"
                                            className="bg-white text-black border border-gray-300 shadow-md font-sans max-w-xs break-words"
                                        >
                                            {name}
                                        </TooltipContent>
                                    </Tooltip>
                                ) : (
                                    <span ref={nameRef} className={cn(
                                        "text-sm truncate block select-none",
                                        is_archived ? "text-muted-foreground" : "text-foreground"
                                    )}>
                                        {name}
                                    </span>
                                )}
                                {path_string && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="text-[10px] text-muted-foreground truncate block select-none cursor-pointer">
                                                {path_string}
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="bottom-start" className="bg-white text-black border border-gray-300 shadow-md font-sans max-w-xs break-words">
                                            {path_string}
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        </div>

                        {/* Metadata row */}
                        {!isMinimalView && (
                            <div className="flex flex-wrap gap-0.5 mt-0.5">
                                {renderAggregatorChips()}
                                {/* Status badge */}
                                {get_status() && (
                                    <span
                                        className="inline-flex items-center px-1.5 py-0 text-[9px] rounded border font-medium"
                                        style={{
                                            borderColor: getStatusColor(),
                                            backgroundColor: `${getStatusColor()}20`,
                                            color: getStatusColor()
                                        }}
                                    >
                                        {get_status()}
                                    </span>
                                )}
                                {data.tags && Array.isArray(data.tags) && data.tags.slice(0, 3).map((tag, index) => (
                                    <span
                                        key={index}
                                        className="inline-flex items-center px-1.5 py-0 text-[9px] rounded border border-border bg-secondary text-secondary-foreground"
                                    >
                                        {tag.name}
                                    </span>
                                ))}
                                {primarySortDateValue && (
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <span className="inline-flex items-center px-1.5 py-0 text-[9px] text-muted-foreground cursor-default">
                                                {sortDateLabel}{' '}
                                                {getRelativeTime(primarySortDateValue)}
                                            </span>
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="bg-popover text-popover-foreground border shadow-md">
                                            {sortDateLabel}{' '}
                                            {formatDateTime(primarySortDateValue)}
                                        </TooltipContent>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Right side - assignee name + unread badge */}
                    <div className="flex items-center justify-end ml-1 flex-shrink-0 gap-1">
                        {!isMinimalView && data.employees && Array.isArray(data.employees) && data.employees.slice(0, 1).map((employee, index) => (
                            <span
                                key={index}
                                className="px-1.5 py-0.5 rounded text-[10px] font-medium text-white truncate max-w-[80px]"
                                style={{ backgroundColor: stringToColor(employee.first_name + employee.last_name) }}
                            >
                                {employee.first_name} {employee.last_name.charAt(0)}{data.employees.length > 1 ? '..' : ''}
                            </span>
                        ))}
                        {/* Unread count badge - hidden in minimal view */}
                        {!isMinimalView && unreadCount > 0 && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-primary text-primary-foreground">
                                        {unreadCount > 99 ? '99+' : unreadCount}
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-popover text-popover-foreground border shadow-md">
                                    {unreadCount} unread comment{unreadCount !== 1 ? 's' : ''}
                                </TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                </div>

                {/* Parent breadcrumb for search results */}
                {depth === -1 && parents && parents.length > 0 && (
                    <div className="ml-2 flex items-center">
                        <span className="text-[10px] text-muted-foreground px-2 py-0.5 bg-muted rounded border border-border truncate max-w-[200px]">
                            {parents.map((parent, index) => (
                                <React.Fragment key={parent.id}>
                                    {index > 0 && ' → '}
                                    {parent.name}
                                </React.Fragment>
                            ))}
                        </span>
                    </div>
                )}
            </div>

            {folderData &&
                <ItemDetailsModal isOpen={isOpen} handleClose={handleClose} depth={depth} itemData={data}
                    itemInfo={folderData}
                    setItemData={handleUpdateFolderData}
                    itemType={'folder'} fetchItemDetails={fetchFolderDetails} />}
        </>
    );
};

export default FolderItem;
